/**
 * Story SF-CHAT-5.2 — lógica do painel de eventos, sem React.
 *
 * Fica separada do componente de propósito: assim o comportamento é testável
 * por comportamento, e não por regex sobre o JSX. O idioma anterior dos testes
 * de frontend aqui era asserção de fonte, que é a mesma fraqueza que as revisões
 * do Epic 4 cobraram no backend — guarda que mede o texto, não a propriedade.
 */

export type Severity = "sucesso" | "recusa" | "erro" | "neutro";

export interface EventPresentation {
  /** Rótulo em pt-BR, na língua do produto. */
  label: string;
  /** Nome do ícone na `lucide-react`. Ícone é desenhado, nunca emoji. */
  icon: string;
  severity: Severity;
}

/** Rótulo textual da severidade: a distinção não pode depender de cor (AC #3). */
export const SEVERITY_LABEL: Record<Severity, string> = {
  sucesso: "ok",
  recusa: "recusa",
  erro: "erro",
  neutro: "passo",
};

export const EVENT_PRESENTATION: Record<string, EventPresentation> = {
  message_received: { label: "Mensagem recebida", icon: "MessageSquare", severity: "neutro" },
  route_decided: { label: "Rota decidida", icon: "GitBranch", severity: "neutro" },
  journey_started: { label: "Jornada ativada", icon: "Route", severity: "neutro" },
  tool_call_started: { label: "Ferramenta chamada", icon: "Wrench", severity: "neutro" },
  tool_call_returned: { label: "Ferramenta respondeu", icon: "CheckCircle2", severity: "sucesso" },
  policy_refusal: { label: "Recusa de política", icon: "ShieldAlert", severity: "recusa" },
  handoff_opened: { label: "Handoff aberto", icon: "UserRound", severity: "recusa" },
  error: { label: "Erro", icon: "AlertTriangle", severity: "erro" },
  turn_finished: { label: "Turno encerrado", icon: "Flag", severity: "neutro" },
};

export interface DescribedEvent extends EventPresentation {
  severityLabel: string;
  /** Verdadeiro quando o backend mandou um tipo que este painel não conhece. */
  unknown: boolean;
}

export function describeEvent(event: { event_type?: unknown }): DescribedEvent {
  const tipo = typeof event?.event_type === "string" ? event.event_type : "";
  const conhecido = EVENT_PRESENTATION[tipo];
  if (conhecido) {
    return { ...conhecido, severityLabel: SEVERITY_LABEL[conhecido.severity], unknown: false };
  }
  // Evento novo no backend **não pode desaparecer** do painel: quem está
  // validando comportamento precisa ver que algo aconteceu, mesmo sem rótulo.
  return {
    label: tipo ? `Evento não reconhecido (${tipo})` : "Evento não reconhecido",
    icon: "HelpCircle",
    severity: "neutro",
    severityLabel: SEVERITY_LABEL.neutro,
    unknown: true,
  };
}

export function formatDuration(ms: unknown): string | null {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) return null;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60000) {
    const segundos = ms / 1000;
    // Vírgula decimal: a língua do produto é pt-BR.
    return `${segundos.toFixed(1).replace(".", ",")} s`;
  }
  const minutos = Math.floor(ms / 60000);
  const resto = Math.round((ms % 60000) / 1000);
  return `${minutos} min ${resto} s`;
}

/**
 * Rótulos legíveis das chaves de detalhe. Chave fora daqui é **ignorada**: o
 * painel não exibe campo que ninguém decidiu exibir, e o contrato da Story 5.1
 * não emite conteúdo de conversa.
 */
const DETAIL_LABEL: Record<string, string> = {
  route: "rota",
  journey: "jornada",
  allowed_tool: "ferramenta permitida",
  tool: "ferramenta",
  result_status: "veredito",
  reason: "motivo",
  item_count: "itens",
  period_start: "início",
  period_end: "fim",
  http_status: "status HTTP",
  error_kind: "tipo do erro",
  message_length: "tamanho da mensagem",
};

const DETAIL_ORDER = Object.keys(DETAIL_LABEL);

export interface DetailPair {
  key: string;
  label: string;
  value: string;
}

export function summarizeDetail(detail: unknown): DetailPair[] {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return [];
  const mapa = detail as Record<string, unknown>;
  const pares: DetailPair[] = [];
  for (const chave of DETAIL_ORDER) {
    if (!(chave in mapa)) continue;
    const valor = mapa[chave];
    // `null` é ausência; `0` e `false` são informação. Testar por veracidade
    // faria "a ferramenta respondeu e não havia nada" desaparecer.
    if (valor === null || valor === undefined) continue;
    pares.push({ key: chave, label: DETAIL_LABEL[chave], value: String(valor) });
  }
  return pares;
}

export interface RuntimeEventLike {
  event_type?: unknown;
  turn_id?: unknown;
  occurred_at?: unknown;
  duration_ms?: unknown;
  detail?: unknown;
}

export interface TurnGroup {
  turnId: string;
  /** Evento que chegou sem identidade de turno; visível, nunca descartado. */
  orphan: boolean;
  events: RuntimeEventLike[];
  finished: boolean;
  totalDurationMs: number | null;
  hasError: boolean;
  hasRefusal: boolean;
  startedAt: string | null;
}

const SEM_TURNO = "__sem_turno__";

/**
 * Agrupa por **identidade de turno**, preservando a ordem de chegada.
 *
 * Story SF-CHAT-5.3. O AC #1 pedia agrupar por `correlation_id`, e ele não serve:
 * é emitido uma vez em `create_session` e é o segundo fator de autenticação, igual
 * em todos os turnos da conversa. Com ele, o painel mostrava "1 turno" depois de
 * duas mensagens. O backend passou a emitir `turn_id` por turno.
 */
export function groupByTurn(events: readonly RuntimeEventLike[]): TurnGroup[] {
  const porTurno = new Map<string, TurnGroup>();
  const ordem: string[] = [];

  for (const evento of events ?? []) {
    // Evento sem identidade **não some**: quem valida comportamento precisa ver
    // que algo aconteceu, mesmo que o backend tenha deixado de carimbar o turno.
    const bruto = typeof evento?.turn_id === "string" ? evento.turn_id : "";
    const turno = bruto || SEM_TURNO;
    let grupo = porTurno.get(turno);
    if (!grupo) {
      grupo = {
        turnId: turno,
        orphan: !bruto,
        events: [],
        finished: false,
        totalDurationMs: null,
        hasError: false,
        hasRefusal: false,
        startedAt: typeof evento.occurred_at === "string" ? evento.occurred_at : null,
      };
      porTurno.set(turno, grupo);
      ordem.push(turno);
    }
    grupo.events.push(evento);
    const descricao = describeEvent(evento);
    if (descricao.severity === "erro") grupo.hasError = true;
    if (descricao.severity === "recusa") grupo.hasRefusal = true;
    if (evento.event_type === "turn_finished") {
      grupo.finished = true;
      grupo.totalDurationMs = typeof evento.duration_ms === "number" ? evento.duration_ms : null;
    }
  }

  return ordem.map((turno) => porTurno.get(turno) as TurnGroup);
}

// ---------------------------------------------------------------------------
// Filtros
// ---------------------------------------------------------------------------

export interface EventFilter {
  key: string;
  label: string;
  /** Tipos que este filtro mostra. Vazio significa "todos". */
  types: readonly string[];
}

export const EVENT_FILTERS: readonly EventFilter[] = [
  { key: "todos", label: "Tudo", types: [] },
  { key: "ferramenta", label: "Ferramenta", types: ["tool_call_started", "tool_call_returned"] },
  { key: "recusa", label: "Recusa", types: ["policy_refusal"] },
  { key: "handoff", label: "Handoff", types: ["handoff_opened"] },
  { key: "erro", label: "Erro", types: ["error"] },
];

export function applyFilter(
  events: readonly RuntimeEventLike[],
  filterKey: string,
): RuntimeEventLike[] {
  const filtro = EVENT_FILTERS.find((candidato) => candidato.key === filterKey);
  // Filtro desconhecido mostra tudo. Esvaziar o painel por um valor inesperado
  // faria parecer que não houve evento nenhum.
  if (!filtro || filtro.types.length === 0) return [...(events ?? [])];
  return (events ?? []).filter(
    (evento) => typeof evento?.event_type === "string" && filtro.types.includes(evento.event_type),
  );
}

/**
 * Quantos erros o filtro está escondendo (AC #4).
 *
 * Esconder erro sem avisar é pior que não filtrar: quem está validando
 * comportamento conclui que o turno correu limpo.
 */
export function hiddenErrorCount(
  events: readonly RuntimeEventLike[],
  filterKey: string,
): number {
  const visiveis = new Set(applyFilter(events, filterKey));
  return (events ?? []).filter(
    (evento) => evento?.event_type === "error" && !visiveis.has(evento),
  ).length;
}

/**
 * Grupos para exibir: as linhas são as do filtro, o **estado é o real**.
 *
 * Visto na tela antes de existir: filtrando por "ferramenta", o `turn_finished`
 * saía da lista e o painel passava a dizer "turno em andamento" para um turno já
 * encerrado. O filtro esconde linhas; não pode reescrever fatos — é a mesma
 * família do AC #4, que proíbe esconder erro sem avisar.
 */
export function groupForDisplay(
  events: readonly RuntimeEventLike[],
  filterKey: string,
): TurnGroup[] {
  const reais = new Map(groupByTurn(events).map((grupo) => [grupo.turnId, grupo]));
  return groupByTurn(applyFilter(events, filterKey)).map((visivel) => {
    const real = reais.get(visivel.turnId);
    if (!real) return visivel;
    return {
      ...visivel,
      finished: real.finished,
      totalDurationMs: real.totalDurationMs,
      hasError: real.hasError,
      hasRefusal: real.hasRefusal,
      startedAt: real.startedAt,
    };
  });
}

export interface StreamSummary {
  totalEvents: number;
  totalTurns: number;
  visibleEvents: number;
  filtered: boolean;
  inFlight: boolean;
  turnsWithError: number;
  hiddenErrors: number;
}

/** Números do fluxo. Os totais são do fluxo inteiro; o recorte é declarado à parte. */
export function summarizeStream(
  events: readonly RuntimeEventLike[],
  filterKey: string,
): StreamSummary {
  const todos = groupByTurn(events);
  const visiveis = applyFilter(events, filterKey);
  return {
    totalEvents: (events ?? []).length,
    totalTurns: todos.length,
    visibleEvents: visiveis.length,
    filtered: visiveis.length !== (events ?? []).length,
    inFlight: todos.some((turno) => !turno.finished),
    turnsWithError: todos.filter((turno) => turno.hasError).length,
    hiddenErrors: hiddenErrorCount(events, filterKey),
  };
}

// ---------------------------------------------------------------------------
// Estado do fluxo
// ---------------------------------------------------------------------------

export type StreamState = "idle" | "connecting" | "live" | "reconnecting" | "down";

export interface StreamStatus {
  state: StreamState;
  attempt?: number;
  retryDelayMs?: number;
}

export type StreamAction =
  | { type: "connect" }
  | { type: "events"; count: number }
  | { type: "failure" }
  | { type: "give_up" };

const BASE_RETRY_MS = 1000;
const MAX_RETRY_MS = 30000;
export const MAX_RETRY_ATTEMPTS = 6;

export function nextStreamState(status: StreamStatus, action: StreamAction): StreamStatus {
  const tentativa = status.attempt ?? 0;
  switch (action.type) {
    case "connect":
      return { state: "connecting", attempt: tentativa };
    case "events":
      // Voltou a receber: o fluxo está vivo e a contagem de tentativas zera,
      // senão uma falha antiga encurtaria a paciência para sempre.
      return { state: "live", attempt: 0 };
    case "failure": {
      const proxima = tentativa + 1;
      if (proxima > MAX_RETRY_ATTEMPTS) {
        return { state: "down", attempt: proxima, retryDelayMs: MAX_RETRY_MS };
      }
      // Recuo exponencial com teto: sem o teto, uma queda longa viraria
      // marteladas no backend; sem o crescimento, viraria martelada imediata.
      const espera = Math.min(BASE_RETRY_MS * 2 ** (proxima - 1), MAX_RETRY_MS);
      return { state: "reconnecting", attempt: proxima, retryDelayMs: espera };
    }
    case "give_up":
      return { state: "down", attempt: tentativa, retryDelayMs: MAX_RETRY_MS };
    default:
      return status;
  }
}

export const STREAM_MESSAGE: Record<StreamState, string> = {
  idle: "Fluxo de eventos parado.",
  connecting: "Conectando ao fluxo de eventos…",
  live: "Fluxo ao vivo.",
  reconnecting: "Fluxo interrompido. Tentando reconectar…",
  down: "Fluxo indisponível. Recarregue a página ou verifique se CHAT_RUNTIME_EVENTS está ligado no backend.",
};
